import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { usePageStatusBar } from '../hooks/usePageStatusBar';
import { PAGE_CHROME_OFF_WHITE } from '../constants/pageChrome';

export function TermsPage() {
  usePageStatusBar({ background: PAGE_CHROME_OFF_WHITE });
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-theme-off-white dark:bg-theme-bg-dark font-bravos">
      <div className="sticky top-0 z-10 bg-theme-off-white dark:bg-theme-bg-dark border-b border-theme-border dark:border-white/10 px-4 py-3 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="p-1.5 -ml-1.5 rounded-xl text-theme-text dark:text-theme-off-white hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
          aria-label="Go back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h1 className="text-base font-semibold text-theme-text dark:text-theme-off-white">Terms of Service</h1>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-8 pb-16">
        <div className="space-y-8 text-sm text-theme-text/80 dark:text-theme-off-white/80 leading-relaxed">

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">1. Acceptance of Terms</h2>
            <p>
              By downloading, installing, or using Bravos ("the App"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the App. These Terms constitute a legally binding agreement between you and Bravos.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">2. Description of Service</h2>
            <p>
              Bravos is a fitness platform that connects members with personal trainers, provides workout planning tools, tracks physical activity, and supports health and wellness goals. The App offers features including workout plans, exercise libraries, progress tracking, and direct messaging with trainers.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">3. Eligibility</h2>
            <p>
              You must be at least 18 years old to use Bravos. By using the App, you represent that you are of legal age and have the full right, power, and authority to enter into and be bound by these Terms. If you are using the App on behalf of an organization, you represent that you have authority to bind that organization.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">4. User Accounts</h2>
            <p>
              To access certain features, you must create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account. Bravos is not liable for any loss resulting from unauthorized use of your account.
            </p>
            <p>
              You agree to provide accurate, current, and complete information during registration and to keep your profile information up to date.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">5. Health & Fitness Disclaimer</h2>
            <p>
              The content provided in Bravos — including workout plans, exercises, and trainer guidance — is for informational and general wellness purposes only. It is not intended to be a substitute for professional medical advice, diagnosis, or treatment.
            </p>
            <p>
              Always consult your physician or a qualified healthcare provider before beginning any new exercise program, especially if you have a pre-existing medical condition, injury, or health concern. You participate in all physical activities at your own risk.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">6. Trainer Relationships</h2>
            <p>
              Trainers on Bravos are independent professionals. Bravos facilitates connections between members and trainers but is not responsible for the quality, safety, or accuracy of advice provided by individual trainers. Members are encouraged to verify trainer credentials independently.
            </p>
            <p>
              Any agreements, payments, or arrangements made outside of the Bravos platform between members and trainers are solely between those parties.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">7. Acceptable Use</h2>
            <p>You agree not to:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Use the App for any unlawful purpose or in violation of any regulations</li>
              <li>Harass, abuse, or harm other users or trainers</li>
              <li>Post false, misleading, or fraudulent content</li>
              <li>Attempt to gain unauthorized access to any part of the App or its systems</li>
              <li>Interfere with or disrupt the integrity or performance of the App</li>
              <li>Scrape, copy, or redistribute content without express written permission</li>
              <li>Impersonate any person or entity</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">8. Intellectual Property</h2>
            <p>
              All content within the App — including text, graphics, logos, icons, images, audio clips, and software — is the property of Bravos or its content suppliers and is protected by applicable intellectual property laws. You may not reproduce, distribute, or create derivative works without our prior written consent.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">9. User Content</h2>
            <p>
              By submitting content to the App (such as profile information, workout logs, or messages), you grant Bravos a non-exclusive, worldwide, royalty-free license to use, display, and process that content solely to provide and improve the App's services. You retain ownership of your content.
            </p>
            <p>
              You are solely responsible for the accuracy and legality of content you submit.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">10. Termination</h2>
            <p>
              We may suspend or terminate your account at any time, with or without notice, for conduct that we determine violates these Terms or is harmful to other users, us, or third parties, or for any other reason at our sole discretion.
            </p>
            <p>
              You may delete your account at any time through the Account settings in the App.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">11. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, Bravos shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to personal injury, loss of data, or loss of revenue, arising out of or in connection with your use of the App.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">12. Changes to Terms</h2>
            <p>
              We may update these Terms from time to time. When we do, we will revise the effective date at the top of this page. Continued use of the App after changes constitutes acceptance of the updated Terms. We encourage you to review these Terms periodically.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-base font-semibold text-theme-text dark:text-theme-off-white">13. Contact</h2>
            <p>
              If you have any questions about these Terms, please contact us at{' '}
              <a href="mailto:support@bravosfit.com" className="text-[var(--ink)] dark:text-[var(--action-primary)] underline underline-offset-2">
                support@bravosfit.com
              </a>.
            </p>
          </section>

        </div>
      </div>
    </div>
  );
}

export default TermsPage;
